import { AppDialogHelper } from 'general/components/AppDialog';
import AppColors from 'general/constants/AppColors';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KTTooltip from '../../KTTooltip';

KTImageInput.propTypes = {
  // required
  name: PropTypes.string.isRequired,

  // optional
  value: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  outlineStyle: PropTypes.bool,
  circleStyle: PropTypes.bool,
  acceptImageTypes: PropTypes.arrayOf(PropTypes.string),
  defaultImage: PropTypes.string,
  editable: PropTypes.bool,
  removable: PropTypes.bool,
  disabled: PropTypes.bool,

  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  enableCheckValid: PropTypes.bool,
  isTouched: PropTypes.bool,
  isValid: PropTypes.bool,
  feedbackText: PropTypes.string,

  fullWidth: PropTypes.bool,
  aspectRatio: PropTypes.string,
  backgroundSize: PropTypes.string,

  onSelectedFile: PropTypes.func,
  onRemovedFile: PropTypes.func,
  onForceRemoveFile: PropTypes.func,
  onPress: PropTypes.func,

  borderStyle: PropTypes.bool,
  additionalInputClassName: PropTypes.string,
  editIconOnTop: PropTypes.bool,
  isUploadIdCard: PropTypes.bool,
  uploadContent: PropTypes.string,
};

KTImageInput.defaultProps = {
  value: '',
  text: '',
  outlineStyle: true,
  circleStyle: false,
  acceptImageTypes: [],
  defaultImage: '',
  editable: false,
  removable: false,
  disabled: false,

  onChange: null,
  onBlur: null,
  onFocus: null,
  enableCheckValid: false,
  isValid: true,
  isTouched: false,
  feedbackText: '',

  fullWidth: false,
  aspectRatio: '1/1',
  backgroundSize: 'cover',

  borderStyle: true,
  additionalInputClassName: '',
  editIconOnTop: true,
  isUploadIdCard: false,
  uploadContent: '',
};

/**
 *
 * @param {{
 * name: string,
 * value: string,
 * text: string | element,
 * outlineStyle: boolean,
 * circleStyle: boolean,
 * acceptImageTypes: string[],
 * editable: boolean,
 * removable: boolean,
 * onChange: function,
 * onBlur: function,
 * onFocus: function,
 * enableCheckValid: boolean,
 * isValid: boolean,
 * isTouched: boolean,
 * feedbackText: string,
 * fullWidth: boolean,
 * aspectRatio: string,
 * backgroundSize: string,
 * onSelectedFile: function,
 * onRemovedFile: function,
 * onForceRemoveFile: function,
 * onPress: function,
 * borderStyle: boolean,
 * additionalInputClassName: string,
 * editIconOnTop: boolean,
 * disabled: boolean,
 * isUploadIdCard: boolean,
 * uploadContent: string,
 * }} props
 * @returns
 */
function KTImageInput(props) {
  // MARK: --- Params ---
  const {
    name,

    value,
    text,
    outlineStyle,
    circleStyle,
    acceptImageTypes,
    defaultImage,
    editable,
    removable,
    disabled,

    onChange,
    onBlur,
    onFocus,
    enableCheckValid,
    isValid,
    isTouched,
    feedbackText,

    fullWidth,
    aspectRatio,
    backgroundSize,

    onSelectedFile,
    onRemovedFile,
    onForceRemoveFile,
    onPress,

    borderStyle,
    additionalInputClassName,
    editIconOnTop,
    isUploadIdCard,
    uploadContent,
  } = props;

  const [selectedImage, setSelectedImage] = useState(null);
  const [inputValue, setInputValue] = useState(value);
  const isShowError = enableCheckValid && !isValid && isTouched && !_.isEmpty(feedbackText);
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // MARK: --- Functions ---
  function handleChange(e) {
    if (!_.isEmpty(inputValue)) {
      URL.revokeObjectURL(inputValue);
    }
    const images = e.target.files;
    if (images && images.length > 0) {
      const selectedImage = images[0];
      setSelectedImage(selectedImage);
      const imageInputValue = URL.createObjectURL(selectedImage);

      setInputValue(imageInputValue);
      if (onChange) {
        onChange(imageInputValue);
      }
      if (onSelectedFile) {
        onSelectedFile(selectedImage);
      }
    }
    if (onFocus) {
      onFocus();
    }
  }

  function handleBlur() {
    if (onBlur) {
      onBlur();
    }
  }

  function handleFocus() {
    if (onFocus) {
      onFocus();
    }
  }

  function handleRemoveImage() {
    if (onForceRemoveFile) {
      onForceRemoveFile();
      return;
    }

    if (!_.isEmpty(inputValue)) {
      URL.revokeObjectURL(inputValue);
    }
    setInputValue('');
    if (onRemovedFile) {
      onRemovedFile();
    }
  }

  const handleDragIn = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
      console.error('No file dropped or invalid file');
      return;
    }

    const file = e.dataTransfer.files[0];

    if (file && acceptImageTypes.includes(file.type)) {
      const imageInputValue = URL.createObjectURL(file);
      setInputValue(imageInputValue);

      if (onChange) {
        onChange(imageInputValue);
      }

      if (onSelectedFile) {
        onSelectedFile(file);
      }
    } else {
      console.error('Invalid file type:', file?.type);
    }
  };

  const handleShowImage = () => {
    AppDialogHelper.show(
      <></>,
      '',
      [],
      false,
      null,
      <div className="d-flex justify-content-center">
        <img src={inputValue} style={{ width: 450 }} />
      </div>
    );
  };

  // MARK: --- Hooks ---

  return (
    <div>
      <div
        className={`
                image-input 
                ${outlineStyle ? 'image-input-outline' : ''}
                ${circleStyle ? 'image-input-circle' : ''}
                ${fullWidth ? 'w-100' : ''}
                ${borderStyle ? 'image-input-border' : ''}
                ${additionalInputClassName}
                ${onPress ? 'cursor-pointer' : ''}
                ${enableCheckValid && isTouched ? `${isValid ? '' : 'image-invalid'}` : ''}
            `}
      >
        {/* wrapper */}
        <div
          className={`${borderStyle ? '' : 'image-input-wrapper'} ${isShowError ? 'border-1 border-danger' : ''
            } ${fullWidth ? 'w-100' : ''} h-auto`}
          style={{
            // backgroundImage: `url(${!_.isEmpty(inputValue) ? inputValue : defaultImage})`,
            aspectRatio: aspectRatio ? aspectRatio : '',
            backgroundSize: backgroundSize,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: isUploadIdCard ? 'pointer' : '',
            border: dragging ? '2px dashed #aaa' : '',
            transition: 'opacity 0.3s ease-in-out',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          onClick={() => {
            if (onPress) {
              onPress();
              return;
            }

            if (isUploadIdCard) {
              if (_.isEmpty(inputValue)) {
                document.getElementById(name)?.click();
              } else {
                handleShowImage();
              }
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDrop={handleDrop}
        >
          <div className='position-relative w-100 h-100'>
            <img
              src={!_.isEmpty(inputValue) ? inputValue : defaultImage}
              alt="avatar"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
            {isUploadIdCard && _.isEmpty(inputValue) && (
              <div className="position-absolute w-100 h-100 top-0 left-0 d-flex flex-column justify-content-center align-items-center gap-2">
                <i
                  className="fa-solid fa-file-arrow-up"
                  style={{
                    fontSize: 36,
                    color: AppColors.gray[600],
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
                <div
                  className="font-weight-bolder"
                  style={{ fontSize: 14, color: AppColors.gray[800] }}
                >
                  Kéo thả hoặc ấn để tải lên
                </div>
              </div>
            )}
          </div>
        </div>
        {!disabled && (!isUploadIdCard || inputValue) && (
          <KTTooltip text={t('change')}>
            <label
              className="btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow"
              data-action={editIconOnTop ? 'change' : 'change-bottom'}
            >
              <i className="fa fa-pen icon-sm text-muted bg-transparent" />

              <input
                type="file"
                name={name}
                id={name}
                accept={_.join(acceptImageTypes, ', ')}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onClick={(e) => {
                  e.target.value = null;
                }}
              />
              <input type="hidden" />
            </label>
          </KTTooltip>
        )}

        {/* editable */}
        {removable && !_.isEmpty(inputValue) && (
          <KTTooltip text={t('remove')}>
            <span
              className={`btn btn-xs btn-icon btn-circle btn-white btn-hover-text-primary btn-shadow`}
              data-action="remove"
              onClick={handleRemoveImage}
            >
              <i className="ki ki-bold-close icon-xs text-muted" />
            </span>
          </KTTooltip>
        )}
      </div>

      {isUploadIdCard && (
        <>
          <input
            type="file"
            name={name}
            id={name}
            accept={_.join(acceptImageTypes, ', ')}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            style={{ display: 'none' }}
            onClick={(e) => {
              e.target.value = null;
            }}
          />

          {!disabled && uploadContent && (
            <div className="d-flex justify-content-center mt-2">
              <div
                className="upload-button text-white px-5 py-2 rounded-pill cursor-pointer d-flex align-items-center font-weight-bolder"
                style={{ backgroundColor: AppColors.info, maxWidth: 'fit-content', fontSize: 14 }}
                onClick={() => {
                  document.getElementById(name)?.click();
                }}
              >
                <i className="fa-solid fa-file-arrow-up text-white mr-2" style={{ fontSize: 13 }} />
                {uploadContent}
              </div>
            </div>
          )}
        </>
      )}

      {/* {isShowError && (
        <div className="fv-plugins-message-container">
          <div className="fv-help-block">{feedbackText}</div>
        </div>
      )} */}
      {!_.isEmpty(text) && (
        <span className="form-text" style={{ color: AppColors.gray[800] }}>
          {text}
        </span>
      )}
    </div>
  );
}

export default KTImageInput;
